import {
  FILTER_YEAR_CONFIG,
  OrderBookConfigurationKeys,
  OrderBookConfigurations,
} from '@epicuro-next/data/order-book';
import { FiltersFieldIds } from '@epicuro-next/models/order-book';
import {
  getValueFromConfigState,
  selectConfigurationsState,
} from '@epicuro-next/platform/configuration';
import { selectLanguage } from '@epicuro-next/platform/i18n';
import {
  selectActiveAccount,
  selectActiveAccountId,
} from '@epicuro-next/state/accounts-state';
import { selectQueryParams } from '@epicuro-next/state/router-store';
import {
  firstDayOfMonthUTC,
  formatDateToLocaleDateUtc,
  formatUTCRange,
  getUTCNow,
  intervalISODateToDateRange,
  isStringDateBefore,
  lastDayOfMonthUTC,
  utcDateTime,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { FilterQuery } from 'graphql-api/baskets-api-interfaces';

import { selectPaginationFilter } from '../../orders';
import { selectOrderBookState } from '../../store';
import { defaultAllValue } from '../constants';
import { filterLabels } from '../filters-labels';
import { filtersFeatureKey } from '../reducer/filters.reducer';
import {
  addDefaultActiveFilters,
  isDefaultFilterValue,
} from '../shared/add-default-active-filters/add-default-active-filters';
import { generateQueryParamsOnFilterUpdate } from '../shared/generate-queryparams-on-filter-update/generate-queryparams-on-filter-update';
import { isDateFilter } from '../shared/is-date-filter/is-date-filter';
import { mapFilterValuesToQueryValues } from '../shared/map-filter-values-to-query/map-filter-values-to-query-values';
import { mapFilterToExportOrderBookV2Request } from '../shared/map-filters-to-export-order-book-request/map-filters-to-export-order-book-request';
import { getYearFilterValue } from '../shared/year-filter/get-year-filter-value';
import { makeYearFilter } from '../shared/year-filter/make-year-filter';
import { makeYearFilterQueryItem } from '../shared/year-filter/make-year-filter-query-item';

export const selectFiltersState = createSelector(
  selectOrderBookState,
  (state) => state[filtersFeatureKey],
);

export const selectLastActivatedFilterId = createSelector(
  selectFiltersState,
  (state) => state.lastActivatedFilterId,
);

export const selectOrderBookFilters = createSelector(
  selectFiltersState,
  (state) => state.filters,
);

export const selectOrderBookFiltersLoaded = createSelector(
  selectFiltersState,
  (state) => state.loaded,
);

export const selectIsOrderBookFiltersLoading = createSelector(
  selectFiltersState,
  (state) => state.isLoading,
);

export const selectAvailablePlacedDateRange = createSelector(
  selectOrderBookFilters,
  (filters) => {
    const placedMonth = filters.find(
      (filter) => filter.fieldId === FiltersFieldIds.PlacedPeriod,
    );
    if (!placedMonth) {
      return null;
    }

    const utcNow = getUTCNow();

    const values = placedMonth.values?.map((f) => f.value) ?? [
      firstDayOfMonthUTC(utcNow),
      lastDayOfMonthUTC(new Date()),
    ];

    const minDate = values[0];

    const maxDate = isStringDateBefore(
      lastDayOfMonthUTC(values[values.length - 1]),
      utcNow,
    )
      ? lastDayOfMonthUTC(values[values.length - 1])
      : utcNow;

    return {
      minDate,
      maxDate,
    };
  },
);

export const selectOrderBookFiltersLabels = createSelector(
  selectOrderBookFilters,
  (filters) =>
    filters.reduce(
      (o, { fieldId, displayName }) => ({
        ...o,
        [fieldId]: filterLabels[fieldId as FiltersFieldIds] ?? displayName,
      }),
      {},
    ),
);

export const selectIsFilterYearEnabled = createSelector(
  selectConfigurationsState,
  (config) => {
    return !!getValueFromConfigState<
      OrderBookConfigurationKeys,
      OrderBookConfigurations
    >(
      config,
      FILTER_YEAR_CONFIG.namespace,
      FILTER_YEAR_CONFIG.featureName,
      FILTER_YEAR_CONFIG.defaultValue,
    );
  },
);

export const selectRegularAvailableOrderBookFilters = createSelector(
  selectOrderBookFilters,
  (filters) => {
    return filters.filter(
      ({ fieldId }) => fieldId !== FiltersFieldIds.PlacedPeriod,
    );
  },
);

export const selectIsYearFilterDefaultValue = createSelector(
  selectFiltersState,
  selectIsFilterYearEnabled,
  (state, isFilterYearEnabled) => {
    const deliveryYearValue = state.activeFilters.find(
      ({ fieldId }) => fieldId === FiltersFieldIds.DeliveryYear,
    );
    if (!deliveryYearValue) {
      return true;
    }
    const defaultValue = getYearFilterValue(
      state.filters,
      [],
      isFilterYearEnabled,
    );
    return (
      deliveryYearValue.value === utcDateTime(defaultValue[0]).year.toString()
    );
  },
);

export const selectAvailableYearsFilter = createSelector(
  selectFiltersState,
  (state) =>
    state.filters.find(
      ({ fieldId }) => fieldId === FiltersFieldIds.DeliveryYear,
    ),
);

export const selectActiveOrderBookFilters = createSelector(
  selectFiltersState,
  selectIsFilterYearEnabled,
  selectActiveAccount,
  (state, filterYearEnabled, activeAccount) => {
    const activeFilters = addDefaultActiveFilters(
      state.filters,
      state.activeFilters,
    );
    if (filterYearEnabled && state.loaded) {
      const hasDeliveryYearSet = activeFilters.find(
        ({ fieldId }) => fieldId === FiltersFieldIds.DeliveryYear,
      );
      if (!hasDeliveryYearSet) {
        activeFilters.push(
          makeYearFilter(
            getYearFilterValue(state.filters, activeFilters, filterYearEnabled),
          ),
        );
      }
    }
    const hasAccountInFilters = state.filters.some(
      (filter) => filter.fieldId === FiltersFieldIds.AccountId,
    );
    const hasAccountInActiveFilters = state.activeFilters.some(
      (filter) => filter.fieldId === FiltersFieldIds.AccountId,
    );

    if (hasAccountInFilters && !hasAccountInActiveFilters) {
      activeFilters.push({
        fieldId: FiltersFieldIds.AccountId,
        value: activeAccount?.accountId ?? '',
      });
    }
    return activeFilters;
  },
);

export const selectSelectedPlacedPeriod = createSelector(
  selectActiveOrderBookFilters,
  (activeFilters) =>
    activeFilters.find(
      ({ fieldId }) => fieldId === FiltersFieldIds.PlacedPeriod,
    ),
);

export const selectSelectedRangeFromPlacedPeriod = createSelector(
  selectSelectedPlacedPeriod,
  (period) => {
    if (period?.value) {
      return intervalISODateToDateRange(period?.value);
    }
    return null;
  },
);

export const selectYearFilterQueryItem = createSelector(
  selectOrderBookFilters,
  selectActiveOrderBookFilters,
  selectIsFilterYearEnabled,
  (availableFilters, activeFilters, filterYearEnabled) => {
    return makeYearFilterQueryItem(
      getYearFilterValue(availableFilters, activeFilters, filterYearEnabled),
    );
  },
);

export const selectRegularActiveOrderBookFilters = createSelector(
  selectActiveOrderBookFilters,
  (activeFilters) => {
    return activeFilters.filter(
      ({ fieldId }) => fieldId !== FiltersFieldIds.PlacedPeriod,
    );
  },
);

export const selectOrderBookSearch = createSelector(
  selectFiltersState,
  (state) => state.search,
);

export const selectQueryParamsByActiveFilters = createSelector(
  selectQueryParams,
  selectActiveOrderBookFilters,
  selectOrderBookSearch,
  (oldParams, filters, search) =>
    generateQueryParamsOnFilterUpdate(oldParams, filters, search),
);

export const selectIsMultipleFilter = (fieldId: string) =>
  createSelector(
    selectRegularAvailableOrderBookFilters,
    (filters) =>
      !!filters.find((filter) => filter.fieldId === fieldId)?.isMultiple,
  );

export const selectOrderFiltersQuery = createSelector(
  selectActiveOrderBookFilters,
  selectOrderBookSearch,
  selectYearFilterQueryItem,
  selectPaginationFilter,
  (filters, search, yearFilter, pagination): FilterQuery => {
    const regularFilters = filters
      .filter((filter) => filter.fieldId !== FiltersFieldIds.DeliveryYear)
      .filter((filter) => filter.value !== defaultAllValue)
      .map((filter) => {
        return {
          fieldId: filter.fieldId,
          values: mapFilterValuesToQueryValues(filter.fieldId, filter.value),
        };
      });
    const hasMonthQuery = regularFilters.some(
      (filter) => filter.fieldId === FiltersFieldIds.DeliveryMonth,
    );

    return {
      filters: hasMonthQuery ? regularFilters : [...regularFilters, yearFilter],
      search,
      pagination,
    };
  },
);

export const selectActiveAggregatedFilters = createSelector(
  selectOrderBookFilters,
  selectActiveOrderBookFilters,
  selectLanguage,
  selectIsYearFilterDefaultValue,
  (originalFilters, filters, locale, isDefaultYearFilter) => {
    const filtersMap = filters.reduce(
      (
        res: { [key: string]: { displayValues: string[]; isDefault: boolean } },
        { fieldId, value },
      ) => {
        if (value === defaultAllValue) {
          return res;
        }

        let filterDisplayValue =
          originalFilters
            .find((f) => f.fieldId === fieldId)
            ?.values?.find((v) => v.value === value)?.displayName ?? value;

        if (fieldId === FiltersFieldIds.PlacedPeriod) {
          filterDisplayValue = formatUTCRange(filterDisplayValue);
        } else if (
          isDateFilter(fieldId) &&
          fieldId !== FiltersFieldIds.DeliveryYear
        ) {
          const date = formatDateToLocaleDateUtc(value, locale, 'MMM yy');
          filterDisplayValue = `${date.charAt(0).toUpperCase()}${date.slice(
            1,
          )}`;
        }

        const isDefaultValue =
          fieldId === FiltersFieldIds.DeliveryYear
            ? isDefaultYearFilter
            : isDefaultFilterValue({ fieldId, value });
        const displayValues = ((res[fieldId] ?? {}).displayValues ?? []).concat(
          filterDisplayValue,
        );

        res[fieldId] = {
          isDefault: isDefaultValue && displayValues.length === 1,
          displayValues,
        };

        return res;
      },
      {},
    );

    return Object.entries(filtersMap).map(
      ([fieldId, { displayValues, isDefault }]) => ({
        fieldId,
        value: displayValues.join(', '),
        isDefault,
      }),
    );
  },
);

export const selectAggregatedPlacedDateRange = createSelector(
  selectActiveAggregatedFilters,
  (activeFilters) =>
    activeFilters.find(
      ({ fieldId }) => fieldId === FiltersFieldIds.PlacedPeriod,
    ),
);

export const selectExportOrderBookV2Request = createSelector(
  selectActiveOrderBookFilters,
  selectOrderBookSearch,
  selectActiveAccountId,
  (filters, search, activeAccountId) => {
    const filtersWithValues = filters
      .filter((filter) => filter.value !== defaultAllValue)
      .map((filter) => {
        return {
          values: mapFilterValuesToQueryValues(filter.fieldId, filter.value),
          fieldId: filter.fieldId,
        };
      });

    const hasAccountFilter = filtersWithValues.some(
      ({ fieldId }) => fieldId === FiltersFieldIds.AccountId,
    );
    const activeFilters = hasAccountFilter
      ? filtersWithValues
      : [
          ...filtersWithValues,
          {
            fieldId: FiltersFieldIds.AccountId,
            values: [activeAccountId as string],
          },
        ];

    return mapFilterToExportOrderBookV2Request(activeFilters, search);
  },
);
